// Angular imports
import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// External libraries
import { countries, formatCountryLabel, FormValidator } from '@tgx/shared/utils';
import { isEqual } from 'lodash';

// Project libraries
import { SelectorOrganization, UserProfileUpdate } from '@tgx/shared/interfaces';
import { ToastsService } from '@tgx/shared/toasts';

// Local imports
import { BillingAccount, BillingAccountCreate, BillingAccountUpdate, Partner } from '../../interface/billing-account';
import { OnboardingService } from '../../services/onboarding.service';
import { OnboardingBillingService } from '../../services/onboarding-billing.service';
import { Steps } from '../../enum/steps';
import { GeoLocationCity } from '../../interface/onboarding';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'tgx-onboarding-company-billing',
  templateUrl: './onboarding-company-billing.component.html',
  styleUrls: ['./onboarding-company-billing.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OnboardingCompanyBillingComponent implements OnInit {
  private fb = inject(FormBuilder);
  private onboardingService = inject(OnboardingService);
  private billingService = inject(OnboardingBillingService);
  private toast = inject(ToastsService);

  @Input() isBuyer: boolean;
  @Input() isSeller: boolean;
  @Input() selectedOrg: SelectorOrganization;
  @Output() changeStep = new EventEmitter<Steps>();
  @Output() updateUserProfile = new EventEmitter<UserProfileUpdate>();

  billingDataForm: FormGroup;
  originalFormData: FormGroup;

  currentCountries: any;
  allCitiesByCountry: GeoLocationCity[] = [];
  buttonName = 'Save & continue';
  partner: Partner;
  isLoading = false;
  citiesLoading = false;
  billingAccount: BillingAccount = null;
  isEditing = false;
  isInitialValue = true;
  backupCountry: string;
  blockCityDropdown = true;
  protected countryNotSelected = true;

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.onboardingService.scrollTop();
    this.buildBillingDataForm();
    this.isEditing = this.onboardingService.getIsEditState();

    this.currentCountries = countries.map((country) => ({
      label: formatCountryLabel(country),
      value: country.isoCode2,
    }));

    this.billingDataForm.get('country').valueChanges.subscribe((country) => {
      if (country && this.backupCountry !== country) {
        this.backupCountry = country;
        this.getCitiesList(country);
      }
    });

    this.initializeBillingData();
  }

  async getCitiesList(countryCode: string) {
    const country = countryCode;

    if (!country) {
      return;
    }

    this.citiesLoading = true;
    this.blockCityDropdown = false;

    try {
      this.allCitiesByCountry = await this.onboardingService.getCitiesByCountryISO(country);
    } catch {
      this.toast.addToast(
        'Error retrieving cities list',
        'bg-danger',
        `There was an error retrieving the cities list for the selected country. Please try again later.`,
      );
    } finally {
      this.citiesLoading = false;
    }
  }

  isButtonDisabled(): boolean {
    const isFormValid = this.billingDataForm.valid;

    if (this.isEditing) {
      return !isFormValid || this.isInitialValue || this.isLoading;
    }
    return !isFormValid || this.isLoading;
  }

  private buildBillingDataForm() {
    this.billingDataForm = this.fb.group({
      fiscalCompanyName: this.fb.control('', [
        FormValidator.required('fiscal company'),
        FormValidator.textInvalidChar('fiscal company'),
      ]),
      streetAddress: this.fb.control('', [
        FormValidator.maxLength('street address', 100),
        FormValidator.required('street address'),
        FormValidator.invalidStreetAddress('street address'),
      ]),
      city: this.fb.control('', [FormValidator.required('city')]),
      country: this.fb.control('', [Validators.required, FormValidator.required('country')]),
      zipCode: this.fb.control('', [
        FormValidator.required('zip code'),
        FormValidator.textOnlyNumbers('zip code'),
        FormValidator.maxLength('zip code', 10),
      ]),
      vatIdNumber: this.fb.control('', [
        FormValidator.required('VAT / ID Number'),
        FormValidator.textInvalidChar('VAT / ID Number'),
        FormValidator.maxLength('VAT / ID Number', 20),
      ]),
    });

    // Disable city selector if no country is selected
    if (this.billingDataForm.get('country').value) {
      this.countryNotSelected = false;
    }
    this.billingDataForm.controls['country'].valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.countryNotSelected = !value;
    });
  }

  getValidationStatus(controlName: string, isRequired: boolean): string {
    return this.onboardingService.getValidationStatus(this.billingDataForm, controlName, isRequired);
  }

  private initializeBillingData() {
    this.billingService
      .getBillingAccounts(this.selectedOrg.code)
      .then(async (accounts) => {
        if (accounts && accounts[0]) {
          this.billingAccount = accounts[0];

          await this.getCitiesList(this.billingAccount.countryCode);
          this.backupCountry = this.billingAccount.countryCode;

          this.billingDataForm.patchValue({
            fiscalCompanyName: this.billingAccount.name,
            streetAddress: this.billingAccount.fiscalAddress,
            city: this.billingAccount.city,
            country: this.billingAccount.countryCode,
            zipCode: this.billingAccount.zipCode,
            vatIdNumber: this.billingAccount.cif,
          });

          Object.keys(this.billingDataForm.controls).forEach((field) => {
            const control = this.billingDataForm.get(field);
            control?.markAsDirty();
          });

          this.buttonName = 'Update';
          this.originalFormData = this.billingDataForm.getRawValue();
          this.billingDataForm.valueChanges.subscribe(() => {
            const currentFormValue = this.billingDataForm.getRawValue();
            this.isInitialValue = isEqual(currentFormValue, this.originalFormData);
          });
        }
      })
      .catch((error) => {
        // Already logged by GqlService.
        this.toast.handleGatewayAndGraphqlErrors(error);
      });
  }

  private parseCreateBillingData(): BillingAccountCreate {
    return {
      name: this.billingDataForm.get('fiscalCompanyName').value,
      cif: this.billingDataForm.get('vatIdNumber').value,
      fiscalAddress: this.billingDataForm.get('streetAddress').value,
      city: this.billingDataForm.get('city').value,
      countryCode: this.billingDataForm.get('country').value,
      singleInvoice: true,
      Partner: { connect: { orgCode: this.selectedOrg.code.toUpperCase() } },
      zipCode: this.billingDataForm.get('zipCode').value,
      excludeVAT: this.isCanaryIslands(),
    };
  }

  private parseUpdateBillingData(): BillingAccountUpdate {
    return {
      name: { set: this.billingDataForm.get('fiscalCompanyName').value },
      cif: { set: this.billingDataForm.get('vatIdNumber').value },
      fiscalAddress: { set: this.billingDataForm.get('streetAddress').value },
      city: { set: this.billingDataForm.get('city').value },
      countryCode: { set: this.billingDataForm.get('country').value },
      zipCode: { set: this.billingDataForm.get('zipCode').value },
      excludeVAT: { set: this.isCanaryIslands() },
    };
  }

  // postal code 35 and 38 is from Canary Islands(Tenerife and La Palma)
  private isCanaryIslands(): boolean {
    const zipCode = this.billingDataForm.get('zipCode')?.value;
    const countryCode = this.billingDataForm.get('countryCode')?.value?.isoCode2;

    return (countryCode === 'ES' && zipCode.slice(0, 2) === '35') || zipCode.slice(0, 2) === '38';
  }

  private async ensurePartner(): Promise<Partner> {
    const partner = await this.billingService.retrievePartner(this.selectedOrg.code);
    if (partner?.id) {
      return partner;
    }
    return this.billingService.createPartner(this.selectedOrg.code, this.selectedOrg.label);
  }

  private async createBilling(formData: BillingAccountCreate): Promise<void> {
    await this.billingService.createBillingAccount(formData);
  }

  private async updateBillingAccount(): Promise<void> {
    const accounts = this.partner?.BillingAccounts || [];
    const promises = [];
    const parseData: BillingAccountUpdate = this.parseUpdateBillingData();
    if (accounts.length > 0) {
      for (const account of accounts) {
        const where: { id: number } = { id: account.id };
        promises.push(this.billingService.updateBillingAccount(where, parseData));
      }
      await Promise.all(promises);
    }
  }

  private vatInputHighlight(error: any) {
    if ((error?.message || error).toLowerCase().includes('vat')) {
      this.billingDataForm.controls['vatIdNumber'].setErrors({
        validateCIF: true,
        errorMsg: 'The VAT/ID number is not valid',
      });
    }
  }

  async submitBilling(): Promise<void> {
    if (this.billingDataForm.invalid) {
      this.billingDataForm.markAllAsTouched();
      return;
    }
    try {
      this.isLoading = true;
      const expectedAccounts = (this.isBuyer ? 1 : 0) + (this.isSeller ? 1 : 0);
      this.partner = await this.ensurePartner();
      const promises = [];

      const parseData: BillingAccountCreate = this.parseCreateBillingData();

      if (this.partner.BillingAccounts?.length === expectedAccounts) {
        await this.updateBillingAccount();
      } else {
        const missing = expectedAccounts - this.partner.BillingAccounts?.length;
        for (let i = 0; i < missing; i++) {
          promises.push(this.createBilling(parseData));
        }
      }

      await Promise.all(promises);

      this.onChangeStep();
    } catch (error: any) {
      this.toast.handleGatewayAndGraphqlErrors(error);
      this.vatInputHighlight(error);
    } finally {
      this.isLoading = false;
    }
  }

  private onChangeStep() {
    if (this.isEditing) {
      this.toast.addToast('Billing data updated successfully', 'bg-success');
    } else {
      this.finishCreatedBillingAccount();
    }
    this.changeStep.emit(Steps.REVIEW);
  }

  private finishCreatedBillingAccount() {
    const date = new Date().toISOString();
    this.updateUserProfile.emit({ onboardingProperties: { billingDataFinishedAt: date } });
  }

  cancelChanges() {
    this.changeStep.emit(Steps.REVIEW);
  }
}
